/**
 * addresses-columns.tsx — budowa `ColumnDefinition[]` dla `ConfigurableTable` z listy
 * pól helper-encji `addresses` (WF NEW 2026-08-20 — migracja gołej `<table>` w
 * `AddressesDynamicTab.tsx` na `ConfigurableTable`, wzorzec: `bank-accounts-columns.tsx`).
 *
 * Kolumny budowane WYŁĄCZNIE z pól `showInList=true` (parytet z dawną gołą tabelą) —
 * GET /addresses zwraca tylko kolumny core, helper-encja adresów nie ma dziś ext-pól
 * w praktyce.
 */

import type { ColumnDefinition } from '@/components/configurable-table';
import type {
  ColumnFilterCategory,
  ColumnHeaderFilterConfig,
} from '@/components/configurable-table/types';
import {
  type DictionaryDetail,
  type DictionaryItem,
  formatDictionaryOptionLabel,
} from '@/hooks/useDictionary';
import { mapExtFieldTypeToFilterCategory, renderBooleanCell } from '@/lib/ext-list-columns';
import { slugToCamel } from '@/lib/slug-to-camel';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export interface SystemModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  showInList: boolean;
  dictionaryCode: string | null;
  dictionaryDisplayMode?: 'label' | 'code' | 'code_label' | null;
  gcrecord: string | null;
}

export interface AddressRow {
  id: string;
  ownerType: string;
  ownerId: string;
  kind: string;
  label: string | null;
  street: string | null;
  buildingNo: string | null;
  apartmentNo: string | null;
  postalCode: string | null;
  city: string | null;
  region: string | null;
  countryCode: string | null;
  isPrimary: boolean;
  isActive: boolean;
  notes: string | null;
}

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  kind: 180,
  is_primary: 100,
  street: 220,
  postal_code: 130,
  city: 160,
  country_code: 100,
};

function readField(row: AddressRow, camelKey: string): unknown {
  return (row as unknown as Record<string, unknown>)[camelKey];
}

/** Etykieta kolumny „Typ adresu" — dołącza chip adresu głównego ZAMIAST osobnej
 *  kolumny „Główny" (dubel informacji, checkbox nic nie mówi o kontekście): dla
 *  adresu macierzystego kontrahenta (`office`) — „Adres macierzysty" (ten sam co
 *  w zakładce „Dane"), dla głównego w obrębie innego typu — „Adres główny". */
function renderKindCell(
  row: AddressRow,
  kindByCode: Map<string, DictionaryItem>,
  kindDictionary: DictionaryDetail | null,
  dictionaryDisplayMode: SystemModuleField['dictionaryDisplayMode'],
  t: TranslateFn,
): React.ReactNode {
  const item = kindByCode.get(row.kind);
  const displayMode = dictionaryDisplayMode ?? kindDictionary?.displayMode ?? 'label';
  const label = item ? formatDictionaryOptionLabel(item, displayMode) : row.kind;
  const isRegistered = row.ownerType === 'contractor' && row.kind === 'office';
  return (
    <span className="inline-flex items-center gap-1">
      <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs bg-bg-secondary text-text-secondary">
        {label}
      </span>
      {row.isPrimary && (
        <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs bg-accent-100 text-accent-700">
          ★{' '}
          {isRegistered
            ? t('tenant.addresses.registeredBadge')
            : t('tenant.addresses.field.isPrimary')}
        </span>
      )}
    </span>
  );
}

export interface BuildAddressColumnsArgs {
  /** Pola `showInList=true` z layoutu, w kolejności DFS (parytet z dawną gołą tabelą). */
  listColumns: SystemModuleField[];
  kindByCode: Map<string, DictionaryItem>;
  kindDictionary: DictionaryDetail | null;
  testIdPrefix: string;
  t: TranslateFn;
}

export function buildAddressColumns({
  listColumns,
  kindByCode,
  kindDictionary,
  testIdPrefix,
  t,
}: BuildAddressColumnsArgs): ColumnDefinition<AddressRow>[] {
  return listColumns
    .filter((field) => field.slug !== 'is_primary') // chip w kolumnie „Typ adresu" zamiast osobnej kolumny
    .map((field) => {
      const camelKey = slugToCamel(field.slug);
      const isKind = field.slug === 'kind';
      const category: ColumnFilterCategory = isKind
        ? 'select'
        : mapExtFieldTypeToFilterCategory(field.fieldType);
      const options =
        isKind && kindDictionary
          ? kindDictionary.items.map((item) => ({
              value: item.code,
              label: formatDictionaryOptionLabel(
                item,
                field.dictionaryDisplayMode ?? kindDictionary.displayMode,
              ),
            }))
          : undefined;
      const headerFilter: ColumnHeaderFilterConfig | undefined =
        category === 'none'
          ? undefined
          : {
              category,
              fieldSlug: field.slug,
              fieldType: field.fieldType,
              ...(options ? { options } : {}),
            };

      return {
        id: field.slug,
        // Surowa wartość (nigdy wyrenderowany glif) — filtr boolean/select porównuje
        // `accessor(row)`, nie output `renderCell`.
        accessor: (row) => readField(row, camelKey),
        header: field.name,
        defaultVisible: true,
        defaultWidth: DEFAULT_COLUMN_WIDTHS[field.slug] ?? 150,
        sortable: true,
        filterable: category === 'select' && !options,
        ...(headerFilter ? { headerFilter } : {}),
        renderCell: (value, row) => (
          <span data-testid={`${testIdPrefix}-row-${row.id}-${field.slug}`}>
            {isKind
              ? renderKindCell(row, kindByCode, kindDictionary, field.dictionaryDisplayMode, t)
              : field.fieldType === 'boolean'
                ? renderBooleanCell(value, t)
                : value === null || value === undefined
                  ? null
                  : String(value)}
          </span>
        ),
      };
    });
}
